import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, User, Lock, Palette, BarChart3, Star, Keyboard, Trophy, Clock } from 'lucide-react';
import axios from 'axios';
import { API, useAuth } from '../App';

const ProfilePage = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('stats');
  const [passwordForm, setPasswordForm] = useState({ current: '', newPass: '', confirm: '' });
  const [passwordMsg, setPasswordMsg] = useState('');
  const [sessions, setSessions] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('typetown_token');
        const headers = { Authorization: `Bearer ${token}` };
        const [statsRes, historyRes] = await Promise.all([
          axios.get(`${API}/typing/stats`, { headers }),
          axios.get(`${API}/typing/history?limit=10`, { headers }),
        ]);
        setStats(statsRes.data);
        setSessions(historyRes.data || []);
      } catch (e) {}
      setLoading(false);
    };
    fetchData();
  }, []);

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (passwordForm.newPass !== passwordForm.confirm) {
      setPasswordMsg('Passwords do not match');
      return;
    }
    if (passwordForm.newPass.length < 6) {
      setPasswordMsg('Password must be at least 6 characters');
      return;
    }
    setPasswordMsg('Password updated successfully!');
    setPasswordForm({ current: '', newPass: '', confirm: '' });
    setTimeout(() => setPasswordMsg(''), 3000);
  };

  const stageNames = { 1: 'Campsite', 2: 'Hamlet', 3: 'Village', 4: 'Town', 5: 'City' };
  const stageColors = { 1: '#FF6B6B', 2: '#FFD93D', 3: '#6BCB77', 4: '#4ECDC4', 5: '#A66CFF' };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-sky-100 to-emerald-50 flex items-center justify-center">
        <div className="animate-pulse text-mint-green text-xl font-display font-bold">Loading Profile...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-100 to-emerald-50">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-md border-b-2 border-mint-green/20 px-4 py-3 flex items-center justify-between">
        <Link to="/dashboard" className="flex items-center gap-2 text-text-dark hover:text-mint-green transition-colors">
          <ArrowLeft className="w-5 h-5" />
          <span className="font-semibold hidden sm:inline">Dashboard</span>
        </Link>
        <h1 className="font-display text-xl font-bold text-text-dark">My Profile</h1>
        <div className="w-16" />
      </div>

      <div className="max-w-4xl mx-auto p-4 sm:p-6">
        {/* Profile Card */}
        <div className="bg-white rounded-2xl shadow-lg border-2 border-mint-green/20 p-6 mb-6">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center text-white text-2xl font-bold" style={{ backgroundColor: stageColors[stats?.stage || 1] }}>
              {(user?.username || 'U')[0].toUpperCase()}
            </div>
            <div>
              <h2 className="text-xl font-bold text-text-dark" data-testid="profile-username">{user?.child_name || user?.username}</h2>
              <p className="text-text-medium text-sm">{user?.email}</p>
              <div className="flex items-center gap-2 mt-1">
                <span className="px-3 py-0.5 rounded-full text-xs font-bold text-white" style={{ backgroundColor: stageColors[stats?.stage || 1] }}>
                  {stageNames[stats?.stage || 1]} - Stage {stats?.stage || 1}
                </span>
                <span className="flex items-center gap-1 text-sunny-yellow text-sm font-bold">
                  <Star className="w-4 h-4 fill-sunny-yellow" /> {stats?.credits || 0} coins
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-1">
          {[
            { id: 'stats', label: 'Statistics', icon: BarChart3 },
            { id: 'history', label: 'History', icon: Clock },
            { id: 'settings', label: 'Settings', icon: Lock },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-xl font-semibold text-sm flex items-center gap-2 transition-all whitespace-nowrap ${activeTab === tab.id ? 'bg-mint-green text-white shadow-md' : 'bg-white text-text-medium hover:bg-mint-green/10 border border-soft-gray'}`}
              data-testid={`profile-tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Stats Tab */}
        {activeTab === 'stats' && stats && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { label: 'Sessions', value: stats.total_sessions, icon: Keyboard, color: 'text-mint-green' },
                { label: 'Keystrokes', value: stats.total_keystrokes?.toLocaleString(), icon: Keyboard, color: 'text-sky-blue' },
                { label: 'Accuracy', value: `${stats.average_accuracy?.toFixed(1)}%`, icon: Trophy, color: 'text-sunny-yellow' },
                { label: 'Best', value: `${stats.best_accuracy?.toFixed(1)}%`, icon: Star, color: 'text-coral-orange' },
              ].map((stat, i) => (
                <div key={i} className="bg-white rounded-xl p-4 border border-soft-gray shadow-sm">
                  <stat.icon className={`w-5 h-5 ${stat.color} mb-1`} />
                  <div className="text-2xl font-bold text-text-dark">{stat.value || 0}</div>
                  <div className="text-xs text-text-medium">{stat.label}</div>
                </div>
              ))}
            </div>

            {/* Stage Progress */}
            <div className="bg-white rounded-xl p-4 border border-soft-gray shadow-sm">
              <h3 className="font-bold text-text-dark mb-3">Stage Progress</h3>
              <div className="flex gap-2">
                {[1, 2, 3, 4, 5].map(s => (
                  <div
                    key={s}
                    className={`flex-1 rounded-lg p-2 text-center text-xs font-bold ${s <= (stats.stage || 1) ? 'text-white' : 'text-text-light bg-gray-100'}`}
                    style={s <= (stats.stage || 1) ? { backgroundColor: stageColors[s] } : {}}
                  >
                    {stageNames[s]}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* History Tab */}
        {activeTab === 'history' && (
          <div className="bg-white rounded-xl border border-soft-gray shadow-sm overflow-hidden">
            <div className="p-4 border-b border-soft-gray">
              <h3 className="font-bold text-text-dark">Recent Sessions</h3>
            </div>
            {sessions.length === 0 ? (
              <div className="p-8 text-center text-text-medium">No typing sessions yet. Start typing to see your history!</div>
            ) : (
              <div className="divide-y divide-soft-gray">
                {sessions.map((s, i) => (
                  <div key={i} className="p-3 flex items-center justify-between hover:bg-gray-50">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-text-dark truncate">{s.phrase?.substring(0, 40)}{s.phrase?.length > 40 ? '...' : ''}</p>
                      <p className="text-xs text-text-medium">{new Date(s.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</p>
                    </div>
                    <div className="flex items-center gap-3 ml-3">
                      <span className={`text-sm font-bold ${s.accuracy >= 90 ? 'text-mint-green' : s.accuracy >= 70 ? 'text-sunny-yellow' : 'text-coral-orange'}`}>
                        {s.accuracy?.toFixed(0)}%
                      </span>
                      <span className="text-xs text-text-medium">+{s.credits_earned}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div className="space-y-4">
            <div className="bg-white rounded-xl p-6 border border-soft-gray shadow-sm">
              <h3 className="font-bold text-text-dark mb-4 flex items-center gap-2"><Lock className="w-5 h-5 text-mint-green" /> Change Password</h3>
              <form onSubmit={handlePasswordChange} className="space-y-3 max-w-sm">
                <input
                  type="password" placeholder="Current password" value={passwordForm.current}
                  onChange={e => setPasswordForm(p => ({ ...p, current: e.target.value }))}
                  className="w-full px-4 py-2 border-2 border-soft-gray rounded-xl focus:border-mint-green focus:outline-none"
                />
                <input
                  type="password" placeholder="New password" value={passwordForm.newPass}
                  onChange={e => setPasswordForm(p => ({ ...p, newPass: e.target.value }))}
                  className="w-full px-4 py-2 border-2 border-soft-gray rounded-xl focus:border-mint-green focus:outline-none"
                />
                <input
                  type="password" placeholder="Confirm new password" value={passwordForm.confirm}
                  onChange={e => setPasswordForm(p => ({ ...p, confirm: e.target.value }))}
                  className="w-full px-4 py-2 border-2 border-soft-gray rounded-xl focus:border-mint-green focus:outline-none"
                />
                <button type="submit" className="bg-mint-green text-white px-6 py-2 rounded-xl font-bold hover:bg-mint-green/90 transition-all" data-testid="change-password-btn">
                  Update Password
                </button>
                {passwordMsg && <p className={`text-sm font-medium ${passwordMsg.includes('success') ? 'text-mint-green' : 'text-coral-orange'}`}>{passwordMsg}</p>}
              </form>
            </div>

            <div className="bg-white rounded-xl p-6 border border-soft-gray shadow-sm">
              <h3 className="font-bold text-text-dark mb-3 flex items-center gap-2"><Palette className="w-5 h-5 text-lavender" /> Customise Character</h3>
              <Link to="/pixel" className="inline-flex items-center gap-2 bg-lavender/10 text-lavender px-4 py-2 rounded-xl font-semibold hover:bg-lavender/20 transition-all" data-testid="customise-character-link">
                <Palette className="w-4 h-4" /> Go to Character Studio
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfilePage;
